import type { PrescriptionImage } from '@/entities/performance/api/performance.api';
import {
  useClinicImages,
  useMonthlySummary,
} from '@/entities/performance/hooks/usePerformanceQueries';
import { useRefreshableImageUrl } from '@/entities/performance/hooks/useRefreshableImageUrl';
import { monthOf } from '@/entities/performance/lib/performance';
import { useBusinessRelationshipList } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import { useDeletePrescriptionImage } from '@/entities/prescription/hooks/usePrescriptionImages';
import { getApiErrorCode, isNetworkError } from '@/shared/api/http-client';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
} from '@/shared/components/StateViews';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button, buttonVariants } from '@/shared/components/ui/button';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { formatDate, toUrlYm } from '@/shared/utils/format';
import { showNetworkErrorToast, toastApi } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

/** 정산 확정 월 — [처방 등록]은 회색 비활성, ✕는 그리지 않는다 (M-07 §3) */
const SETTLED_REASON = '정산이 확정된 달은 등록 · 삭제할 수 없어요.';

/** 내 거래처가 아닌 병의원 — 처방 등록 API 경로(relationshipId)가 없다 */
const NO_RELATIONSHIP_REASON =
  '거래처로 등록된 병의원이 아니라 등록할 수 없어요.';

/**
 * 처방 이미지 상태 배지 (실적_처방이미지 737:6705) — 확인 완료(초록) · 확인 중(노랑) · 처방 제외(빨강)
 * 기획 변경: 「재등록 필요」 → 「처방 제외」 + 제외 사유. VERIFIED만 확인 완료, REJECTED는 처방 제외.
 */
export const ImageStatusBadge = ({ image }: { image: PrescriptionImage }) =>
  image.reviewStatusCode === 'VERIFIED' ? (
    <Badge state="success" emphasis="strong">
      확인 완료
    </Badge>
  ) : image.reviewStatusCode === 'REJECTED' ? (
    <Badge state="error" emphasis="strong">
      처방 제외
    </Badge>
  ) : (
    <Badge state="warning" emphasis="strong">
      확인 중
    </Badge>
  );

/** 썸네일 — presigned URL이 만료되면 다시 받는다. 원본이 없으면 회색 칸 */
const Thumbnail = ({
  ym,
  clinicId,
  image,
}: {
  ym: string;
  clinicId: number;
  image: PrescriptionImage;
}) => {
  const { src, onError } = useRefreshableImageUrl(ym, clinicId, image);
  return src ? (
    <img
      src={src}
      alt={`처방 이미지 ${image.displayName}`}
      loading="lazy"
      onError={onError}
      className="size-full object-cover"
    />
  ) : (
    <span className="text-body-13-r text-fg-light">
      이미지를 불러올 수 없어요
    </span>
  );
};

/**
 * M-16 처방 이미지 탭 (Figma 735:5498) — 이미지 카드 목록, 카드를 누르면 M-18 처방 이미지 상세 페이지
 * 원본 파일명은 저장되지 않아 서버 displayName(이미지 코드)을 라벨로 쓴다.
 *
 * 처방 수정(M-07 §3): 처방 확인 전(isConfirmed === false) 카드의 [이미지 삭제]로 지운다.
 * 상세(M-18)에는 삭제 진입점을 두지 않는다.
 */
export const ImagesTab = ({
  ym,
  clinicId,
  clinicName,
  relationshipId,
}: {
  ym: string;
  clinicId: number;
  clinicName: string;
  /** 본인 거래처 ID — 없으면 [처방 등록] 비활성 */
  relationshipId: number | null;
}) => {
  const images = useClinicImages(ym, clinicId);
  // ClinicSummary와 같은 쿼리(캐시 공유) — 정산 확정 여부
  const monthly = useMonthlySummary(ym, clinicId);
  const openUpload = useUIStore((s) => s.openPrescriptionUpload);
  const deleteImage = useDeletePrescriptionImage();
  // 처방 등록 팝업의 등록 가능 판정(statusView)은 거래처 목록 값을 쓴다
  const relationships = useBusinessRelationshipList();
  const statusView =
    relationships.data?.items.find((r) => r.relationshipId === relationshipId)
      ?.statusView ?? '거래 중';
  const [deleting, setDeleting] = useState<PrescriptionImage | null>(null);

  const settled = !!monthly.data?.hasSettlement;
  // 확정 여부를 모르는 동안에는 ✕를 그리지 않는다 (서버도 확정 이미지 삭제는 막는다)
  const canDelete = !monthly.isPending && !settled;

  // M-06 처방 등록 모달 — 병의원을 이어받는다.
  // 정산월은 서버가 정하므로 보던 달과 다르게 들어가면 완료 스낵바로 알린다
  const register = () =>
    relationshipId !== null &&
    openUpload({
      relationshipId,
      clinicName,
      statusView,
      viewingSettlementYearMonth: ym,
    });

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteImage.mutateAsync(deleting.imageCode);
      toastApi.success('1장이 삭제되었어요.');
    } catch (error) {
      if (getApiErrorCode(error) === 'PRESCRIPTION_ALREADY_EXISTS') {
        toastApi.error('이미 처방 확인이 끝난 이미지라 삭제할 수 없어요.');
        void images.refetch();
      } else if (isNetworkError(error)) {
        showNetworkErrorToast();
      } else {
        toastApi.error(
          '이미지를 삭제하지 못했어요. 잠시 후 다시 시도해주세요.',
        );
      }
    } finally {
      setDeleting(null);
    }
  };

  const list = images.data ?? [];

  const registerButton = (
    <Button
      disabled={settled || relationshipId === null}
      onClick={register}
      className="shrink-0"
    >
      처방 등록
    </Button>
  );
  const blockedReason = settled
    ? SETTLED_REASON
    : relationshipId === null
      ? NO_RELATIONSHIP_REASON
      : null;

  return (
    <div className="flex flex-col gap-6 px-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4">
          <p className="flex items-center gap-2 text-subtitle-18-b">
            <span className="text-fg-primary">{monthOf(ym)}월 처방 이미지</span>
            <span className="text-fg-accent">{list.length}장</span>
          </p>
          {registerButton}
        </div>
        {blockedReason && (
          <p className="text-right text-body-13-r text-fg-tertiary">
            {blockedReason}
          </p>
        )}
      </div>

      {images.isPending ? (
        <LoadingBlock className="h-[184px]" />
      ) : images.isError ? (
        <ErrorState onRetry={() => void images.refetch()} />
      ) : list.length === 0 ? (
        <EmptyState
          className="rounded-12 border border-dashed border-line-grey py-14"
          message="이 달에 등록한 처방 이미지가 없어요."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {list.map((img) => (
            <li
              key={img.imageCode}
              className="relative flex gap-3 rounded-12 border border-line-grey p-4 transition-colors hover:bg-surface-grey"
            >
              <span className="flex h-[150px] w-[200px] shrink-0 items-center justify-center overflow-hidden rounded-8 bg-surface-grey">
                <Thumbnail ym={ym} clinicId={clinicId} image={img} />
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <span className="self-start">
                    <ImageStatusBadge image={img} />
                  </span>
                  {img.reviewStatusCode === 'REJECTED' && img.rejectReason && (
                    <p className="text-body-13-sb text-fg-error">
                      {img.rejectReason}
                    </p>
                  )}
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  {/* 카드 전체를 덮는 링크 — M-18 처방 이미지 상세 페이지 */}
                  <Link
                    to={`${ROUTE_PATHS.PERFORMANCE_MONTH}/images/$imageCode`}
                    params={{
                      yearMonth: toUrlYm(ym),
                      imageCode: img.imageCode,
                    }}
                    search={{ clinicId }}
                    className="truncate text-body-13-sb text-fg-primary outline-none after:absolute after:inset-0 after:rounded-12 focus-visible:after:shadow-[0_0_0_3px_var(--color-brand-200)]"
                  >
                    {img.displayName}
                  </Link>
                  <span className="text-body-13-r text-fg-secondary">
                    {formatDate(img.createdAt)}
                  </span>
                </div>
              </div>
              {/* 처방 확인 전 이미지에만 — 확인 완료 · 정산 확정 월은 그리지 않는다 */}
              {canDelete && img.isConfirmed === false && (
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setDeleting(img)}
                  aria-label={`${img.displayName} 삭제`}
                  className="relative z-10 shrink-0 self-start"
                >
                  이미지 삭제
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* M-07 §3 삭제 확인 팝업 (Pop-Up 848:9402) — [취소] 회색 · [삭제] 빨강 */}
      <AlertDialog
        open={!!deleting}
        onOpenChange={(o) => !o && !deleteImage.isPending && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogTitle>이미지를 삭제할까요?</AlertDialogTitle>
          <AlertDialogDescription className="break-all">
            {deleting?.displayName} 1장을 삭제해요.
            <br />
            삭제하면 되돌릴 수 없고, 그 달 등록 장수에서 빠져요.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteImage.isPending}>
              취소
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteImage.isPending}
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              className={buttonVariants({ variant: 'error', size: 'large' })}
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
